"use client"

import React, { useCallback, useEffect, useState } from "react"
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet"
import L from "leaflet"
import axios from "axios"
import "leaflet/dist/leaflet.css"
import { useAuth } from "../context/AuthContext"
import LanguageSwitcher from "./LanguageSwitcher"
import { useTranslation } from "react-i18next"
import i18n from "../i18n"

const PLANT_COLORS = {
  "bagrem": "#f97316",
  "livada": "#22c55e",
  "kadulja": "#8b5cf6",
  "draca": "#ef4444",
  "kesten": "#92400e",
}

const PLANT_ICONS = {
  "Wildflower": "https://cdn-icons-png.flaticon.com/512/3577/3577932.png",
  "Thyme": "https://cdn-icons-png.flaticon.com/512/4788/4788191.png",
  "Mint": "https://cdn-icons-png.flaticon.com/512/6692/6692125.png",
  "Clover": "https://cdn-icons-png.flaticon.com/512/15465/15465740.png",
  "Acacia": "https://cdn-icons-png.flaticon.com/512/4191/4191618.png",
  "Pine": "https://cdn-icons-png.flaticon.com/512/4731/4731692.png",
  "Spruce": "https://cdn-icons-png.flaticon.com/512/2982/2982280.png",
  "Heather": "https://cdn-icons-png.flaticon.com/512/16088/16088527.png",
  "Rosemary": "https://cdn-icons-png.flaticon.com/512/12713/12713318.png",
  "Yarrow": "https://cdn-icons-png.flaticon.com/512/14437/14437655.png",
  "Sage": "https://cdn-icons-png.flaticon.com/512/10979/10979570.png",
  "Wild Thyme": "https://cdn-icons-png.flaticon.com/512/4788/4788191.png",
  "Wild Mint": "https://cdn-icons-png.flaticon.com/512/6692/6692125.png",
  "Lavender": "https://cdn-icons-png.flaticon.com/512/3977/3977026.png",
  "Sunflower": "https://cdn-icons-png.flaticon.com/512/3577/3577929.png",
  "Linden": "https://cdn-icons-png.flaticon.com/512/4191/4191618.png",
  "Chestnut": "https://cdn-icons-png.flaticon.com/512/4731/4731692.png",
}

const FILTER_OPTIONS = [
  { key: "top3", label: "Top 3", emoji: "🏆", color: "#f59e0b" },
  { key: "bagrem", label: "bagrem", emoji: "🌸", color: "#f97316" },
  { key: "draca", label: "draca", emoji: "🌵", color: "#ef4444" },
  { key: "livada", label: "livada", emoji: "🌼", color: "#22c55e" },
  { key: "kesten", label: "kesten", emoji: "🌰", color: "#92400e" },
  { key: "kadulja", label: "kadulja", emoji: "🌱", color: "#8b5cf6" },
]

// ─── BEE CONDITIONS LOGIC ───────────────────────────────────────────────────────
const calcBeeConditions = (weather, t) => {
  if (!weather) return null
  const { temp, wind_speed, description } = weather
  const hour = new Date().getHours()

  if (hour < 6 || hour > 20) {
    return {
      score: 0,
      label: t("Noćni uslovi"),
      sublabel: t("Pčele miruju"),
      color: "#6366f1",
      emoji: "🌙",
      bars: 0
    }
  }

  let score = 100

  if (temp < 8) score -= 80
  else if (temp < 13) score -= 50
  else if (temp < 18) score -= 20
  else if (temp > 33) score -= 40
  else if (temp > 28) score -= 10

  if (wind_speed > 12) score -= 60
  else if (wind_speed > 8) score -= 35
  else if (wind_speed > 5) score -= 15
  else if (wind_speed > 3) score -= 5

  const cond = (description || "").toLowerCase()
  if (cond.includes("storm") || cond.includes("thunder")) score -= 60
  else if (cond.includes("rain") || cond.includes("shower")) score -= 40
  else if (cond.includes("snow") || cond.includes("sleet")) score -= 70
  else if (cond.includes("fog") || cond.includes("mist")) score -= 15
  else if (cond.includes("overcast") || cond.includes("broken")) score -= 5
  else if (cond.includes("clear") || cond.includes("sun")) score += 5

  score = Math.max(0, Math.min(100, score))

  if (score >= 80) return { score, label: t("Odlični uslovi"), sublabel: t("Idealan period za pčele"), color: "#22c55e", emoji: "🐝", bars: 5 }
  if (score >= 60) return { score, label: t("Dobri uslovi"), sublabel: t("Pčele aktivne i produktivne"), color: "#84cc16", emoji: "🐝", bars: 4 }
  if (score >= 40) return { score, label: t("Umjereni uslovi"), sublabel: t("Ograničena aktivnost"), color: "#f59e0b", emoji: "🐝", bars: 3 }
  if (score >= 20) return { score, label: t("Loši uslovi"), sublabel: t("Nepovoljno za pčele"), color: "#f97316", emoji: "😴", bars: 2 }
  return { score, label: t("Vrlo loši uslovi"), sublabel: t("Pčele ostaju u košnici"), color: "#ef4444", emoji: "🏠", bars: 1 }
}

const BeeConditionsCard = ({ weather }) => {
  const { t } = useTranslation()
  const conditions = calcBeeConditions(weather, t)
  const accent = conditions?.color || "#f59e0b"

  return (
    <div
      className="rounded-2xl overflow-hidden relative min-h-[150px]"
      style={{
        background: `linear-gradient(135deg, ${accent}1a 0%, ${accent}08 60%, rgba(255,251,235,0.9) 100%)`,
        border: `1px solid ${accent}35`,
        boxShadow: `0 8px 32px ${accent}18, 0 2px 8px rgba(0,0,0,0.05)`,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none rounded-2xl"
        style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.0) 55%)" }}
      />
      <div className="relative z-10 p-5 h-full flex flex-col">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${accent}28, ${accent}0d)`, border: `1px solid ${accent}30` }}
          >
            {conditions ? conditions.emoji : "🐝"}
          </div>
          <div>
            <div className="text-sm font-bold text-gray-800">{t("Vremenski uslovi za pčele")}</div>
            <div className="text-xs text-gray-500">
              {weather
                ? `${Math.round(weather.temp)}°C · ${t("vjetar")} ${Math.round(weather.wind_speed)} m/s`
                : t("Odaberi lokaciju za procjenu")}
            </div>
          </div>
        </div>
        {conditions ? (
          <>
            <div className="flex gap-1.5 mb-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className="flex-1 rounded-full transition-all duration-700"
                  style={{
                    height: 7,
                    backgroundColor: i <= conditions.bars ? accent : `${accent}1a`,
                    boxShadow: i <= conditions.bars ? `0 0 8px ${accent}55` : "none",
                  }}
                />
              ))}
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs font-bold" style={{ color: accent }}>{conditions.label}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{conditions.sublabel}</div>
              </div>
              <div className="text-2xl font-black tabular-nums leading-none" style={{ color: accent }}>
                {conditions.score}
                <span className="text-sm font-semibold">%</span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-3 flex-1 flex flex-col items-center justify-center">
            <div className="text-2xl mb-1">🐝</div>
            <p className="text-xs text-gray-400">{t("Odaberi lokaciju na mapi")}</p>
          </div>
        )}
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 rounded-b-2xl"
        style={{ height: 3, background: `linear-gradient(to right, transparent, ${accent}70, transparent)` }}
      />
    </div>
  )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const RecommendationMap = () => {
  const { user } = useAuth()
  const [userHives, setUserHives] = useState([])
  const [recommendations, setRecommendations] = useState({ Bosnia: [], Croatia: [], Serbia: [] })
  const [selected, setSelected] = useState(null)
  const [weather, setWeather] = useState(null)
  const [activeCountry, setActiveCountry] = useState("Croatia")
  const { t } = useTranslation()
  const [satelliteData, setSatelliteData] = useState([])
  const [forecast, setForecast] = useState(null)
  const [forageZones, setForageZones] = useState([])
  const [activeFilter, setActiveFilter] = useState("top3")

  const redIcon = new L.Icon({ iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png", iconSize: [32, 32] })
  const blueIcon = new L.Icon({ iconUrl: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png", iconSize: [32, 32] })
  const greenIcon = new L.Icon({ iconUrl: "https://maps.google.com/mapfiles/ms/icons/green-dot.png", iconSize: [32, 32] })
  const hiveIcon = new L.Icon({ iconUrl: "https://cdn-icons-png.flaticon.com/512/8607/8607015.png", iconSize: [32, 32] })

  const fetchUserHives = useCallback(() => {
    if (!user?.token) return
    fetch(`${process.env.REACT_APP_API_URL}/hives`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setUserHives(data)
        else if (Array.isArray(data.hives)) setUserHives(data.hives)
        else setUserHives([])
      })
      .catch(err => console.error("Hive fetch error:", err))
  }, [user])

  useEffect(() => { fetchUserHives() }, [fetchUserHives])

  useEffect(() => {
    const fetchRecommendations = () => {
      fetch(`${process.env.REACT_APP_API_URL}/recommendations`)
        .then(res => res.json())
        .then(data => setRecommendations(data))
        .catch(err => console.error("GPT recommendation error:", err))
    }
    fetchRecommendations()
    const interval = setInterval(fetchRecommendations, 30 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/forage-zones`)
      .then(res => setForageZones(res.data))
      .catch(err => console.error("Forage zones error:", err))
  }, [])

  useEffect(() => {
    if (!selected) return
    const apiKey = process.env.REACT_APP_OPENWEATHER_API_KEY
    const url = `https://api.openweathermap.org/data/2.5/weather?lat=${selected.lat}&lon=${selected.lng}&units=metric&appid=${apiKey}`
    axios.get(url)
      .then(response => {
        const w = response.data
        setWeather({
          description: w.weather[0].description,
          temp: w.main.temp,
          wind_speed: w.wind.speed,
          rainfall: w.rain?.["1h"] ?? 0,
          humidity: w.main.humidity,
        })
      })
      .catch(() => setWeather(null))
  }, [selected])

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/satellite`)
      .then(res => setSatelliteData(res.data))
      .catch(err => console.error("Satellite data fetch error:", err))
  }, [])

  useEffect(() => {
    if (!selected) return
    const loadForecast = async () => {
      try {
        const data = await fetchForecast(selected.lat, selected.lng)
        setForecast(data.forecast)
      } catch (err) {
        setForecast(null)
      }
    }
    loadForecast()
  }, [selected])

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A"
    const [year, month, day] = dateStr.split("-")
    return `${day}.${month}.${year}`
  }

  const FlyToMarker = ({ lat, lng }) => {
    const map = useMap()
    useEffect(() => {
      if (lat && lng) map.flyTo([lat, lng], 8)
    }, [lat, lng, map])
    return null
  }

  const findSatelliteMetrics = () => {
    if (!selected || satelliteData.length === 0) return null
    let metric = satelliteData.find(d => d.city_name === selected.name)
    if (!metric) {
      const toRad = v => (v * Math.PI) / 180
      const distance = (lat1, lon1, lat2, lon2) => {
        const R = 6371
        const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1)
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      }
      let closest = null, minDist = Infinity
      satelliteData.forEach(d => {
        const dist = distance(selected.lat, selected.lng, d.lat, d.lon)
        if (dist < minDist) { minDist = dist; closest = d }
      })
      metric = closest
    }
    return metric
  }

  const satelliteMetrics = findSatelliteMetrics()

  const getWeatherImage = () => {
    if (!weather) return "/weather.jpg"
    const c = weather.description.toLowerCase()
    if (c.includes("sun") || c.includes("clear")) return "/sunny.png"
    if (c.includes("cloud")) return "/cloudy.png"
    if (c.includes("rain")) return "/rainy.png"
    if (c.includes("wind")) return "/wind.jpg"
    if (c.includes("snow")) return "/snow.png"
    if (c.includes("mist")) return "/mist.png"
    return "/weather.jpg"
  }

  const getDayName = date => {
    const d = new Date(date)
    const currentLang = i18n.language || "en"
    const locale = currentLang === "bs" || currentLang === "hr" ? "hr" : "en"
    return d.toLocaleDateString(locale, { weekday: "short" })
  }

  const PLANT_ICONS_LOCAL = PLANT_ICONS

  const fetchForecast = async (lat, lng) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/forecast7?lat=${lat}&lng=${lng}`)
    return await res.json()
  }

  const getWeatherIcon = condition => {
    if (!condition) return "🌤️"
    const c = condition.toLowerCase()
    if (c.includes("sun") || c.includes("clear")) return "☀️"
    if (c.includes("cloud")) return "☁️"
    if (c.includes("rain")) return "🌧️"
    if (c.includes("snow")) return "❄️"
    if (c.includes("wind")) return "💨"
    if (c.includes("storm")) return "⛈️"
    return "🌤️"
  }

  const getForecastIcon = day => {
    if (!day) return "🌤️"
    if (day.condition) return getWeatherIcon(day.condition)
    const avg = (day.temp_min + day.temp_max) / 2
    if (avg > 25) return "☀️"
    if (avg > 15) return "🌤️"
    if (avg > 5) return "☁️"
    if (day.precip > 5) return "🌧️"
    return "🌤️"
  }

  const visibleForageZones = activeFilter === "top3" ? [] : forageZones.filter(z => z.plant_type === activeFilter)
  const filteredZonesList = forageZones.filter(z => z.plant_type === activeFilter)
  const activeFilterConfig = FILTER_OPTIONS.find(f => f.key === activeFilter)

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-white">
      {/* HEADER */}
      <header className="sticky top-0 z-[1000] bg-white/90 backdrop-blur-sm border-b border-amber-100 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
                  <span className="text-white font-bold text-lg">HS</span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div><p className="text-sm text-gray-600">{t("Welcome back")}</p></div>
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">HoneyScout</h1>
              <p className="text-sm text-gray-600 mt-1">{t("Honeyscout — This week's new prediction.")}</p>
            </div>
            <div className="flex items-center gap-3"><LanguageSwitcher /></div>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="container mx-auto px-4 py-6">
        <section className="mb-6">
          <a
            href="https://beecare.io/beecare-standalone-scale/"
            target="_blank"
            rel="noreferrer"
            className="block"
          >
            <div className="relative overflow-hidden rounded-2xl border border-amber-200 bg-cover bg-center cursor-pointer hover:shadow-xl transition-shadow" style={{ backgroundImage: "url('/template.png')" }}>
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 lg:gap-5 p-2.5 sm:p-3 md:p-4 lg:p-5 items-center">
                <div className="relative z-20 space-y-2 sm:space-y-2.5 md:space-y-3 lg:space-y-4 lg:pl-6">
                  <img
                    src="/Bee%20Care%20Logo.png"
                    alt="BeeCare logo"
                    className="h-12 sm:h-14 md:h-16 w-auto mx-auto lg:mx-0"
                  />
                  <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-gagalin font-extrabold tracking-tight text-slate-900 uppercase leading-tight text-center lg:text-left sm:whitespace-nowrap">
                    <span className="inline">{t("DIGITALNI SISTEMI")}</span>
                    <span
                      className="block sm:inline font-gagalin px-2 py-1 font-black text-slate-900 rounded-lg mt-2 sm:mt-0 sm:ml-2"
                      style={{ backgroundColor: '#ffbd59' }}
                    >
                      {t("ZA PČELARSTVO")}
                    </span>
                  </h2>
                  <p className="text-xs sm:text-sm md:text-sm lg:text-base font-poppins text-slate-700 italic leading-snug text-center lg:text-left">
                    {t("Želite pratiti stanje svojih košnica u realnom vremenu?")} <br />
                    {t("Kliknite i naručite")} <strong className="not-italic">BeeCare</strong> {t("Scale!")}
                  </p>
                  <div className="flex justify-center lg:justify-start mt-1.5">
                    <span
                      className="inline-flex items-center justify-center gap-2 px-3.5 sm:px-4 py-1.5 rounded-full 
              text-xs sm:text-sm lg:text-sm font-bold text-white bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 
              shadow-lg shadow-orange-300/40 hover:scale-[1.01] transition-transform whitespace-nowrap"
                    >
                      {t("Naruči")} <span className="text-sm">≫≫</span>
                    </span>
                  </div>
                </div>

                <div className="relative z-10 flex items-center justify-center lg:justify-end h-44 sm:h-40 md:h-44 lg:h-[20vh] overflow-hidden lg:pr-12 xl:pr-16">
                  <img
                    src="/Vaga.png"
                    alt="BeeCare vaga"
                    className="h-44 sm:h-40 md:h-44 lg:h-[30vh] w-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </a>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* ══ LEFT SIDEBAR ══ */}
          <div className="lg:col-span-3 flex flex-col gap-6">

            {/* FILTER */}
            <div className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center">
                    <span className="text-white text-sm">🔍</span>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-800">{t("Filter Locations")}</h2>
                    <p className="text-xs text-gray-500">{t("Choose view type")}</p>
                  </div>
                </div>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {FILTER_OPTIONS.map((option) => {
                  const isActive = activeFilter === option.key
                  return (
                    <button
                      key={option.key}
                      onClick={() => { setActiveFilter(option.key); setSelected(null) }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border ${isActive ? "text-white shadow-md scale-[1.02]" : "bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50"}`}
                      style={isActive ? { backgroundColor: option.color, borderColor: option.color } : {}}
                    >
                      <span className="text-base leading-none">{option.emoji}</span>
                      <span className="truncate">{t(option.label)}</span>
                      {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white opacity-80 flex-shrink-0"></span>}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* TOP 3 */}
            {activeFilter === "top3" && (
              <div className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                  <h2 className="text-lg font-bold text-gray-800">{t("Top 3 Locations")}</h2>
                  <p className="text-xs text-gray-500">{t("Best locations for beekeeping")}</p>
                </div>
                <div className="flex bg-white border-b">
                  {["Bosnia", "Croatia", "Serbia"].map((country) => (
                    <button key={country} onClick={() => setActiveCountry(country)}
                      className={`flex-1 px-2 py-3 text-xs font-medium transition-all ${activeCountry === country ? "bg-amber-500 text-white shadow-inner" : "text-gray-600 hover:bg-amber-50"}`}>
                      {t(country)}
                    </button>
                  ))}
                </div>
                <div className="p-3 space-y-2">
                  {recommendations[activeCountry]?.slice(0, 3).map((spot, index) => {
                    const isSelected = selected?.name === spot.name && selected?.country === activeCountry
                    return (
                      <button key={index}
                        onClick={() => setSelected({ ...spot, country: activeCountry, source: "gpt", rank: index + 1 })}
                        className={`w-full text-left p-3 rounded-xl transition-all duration-200 ${isSelected ? "bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-400 shadow-md" : "bg-white border border-gray-200 hover:border-amber-300 hover:shadow-sm"}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-600"}`}>
                              <span className="font-bold text-sm">{index + 1}</span>
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-800 text-sm">{spot.name}</h3>
                              <p className="text-xs text-gray-500">{t(activeCountry)}</p>
                            </div>
                          </div>
                          {isSelected && <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* PLANT ZONES */}
            {activeFilter !== "top3" && (
              <div className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
                <div className="p-4 border-b"
                  style={{ background: `linear-gradient(to right, ${activeFilterConfig?.color}18, ${activeFilterConfig?.color}08)`, borderColor: `${activeFilterConfig?.color}30` }}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{activeFilterConfig?.emoji}</span>
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">{t(activeFilterConfig?.label)}</h2>
                      <p className="text-xs text-gray-500">{filteredZonesList.length} {t("locations found")}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                  {filteredZonesList.length === 0 ? (
                    <div className="text-center py-6"><p className="text-gray-400 text-sm">{t("No zones found for this plant")}</p></div>
                  ) : filteredZonesList.map((zone, index) => {
                    const color = PLANT_COLORS[zone.plant_type] || "#22c55e"
                    const isSelected = selected?.id === zone.id
                    return (
                      <button key={zone.id || index}
                        onClick={() => setSelected({ ...zone, lat: zone.latitude, lng: zone.longitude, source: "zone" })}
                        className={`w-full text-left p-3 rounded-xl transition-all duration-200 border ${isSelected ? "border-2 shadow-md" : "border-gray-200 hover:shadow-sm"}`}
                        style={isSelected ? { borderColor: color, backgroundColor: color + "10" } : {}}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + "25" }}>
                              <img src={PLANT_ICONS[zone.plant_type] || PLANT_ICONS["Wildflower"]} alt={zone.plant_type} className="w-4 h-4 object-contain" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-800 text-sm">{zone.name}</h3>
                              <p className="text-xs text-gray-400">{t(zone.country)} · {(zone.radius / 1000).toFixed(0)} km</p>
                            </div>
                          </div>
                          {isSelected && <div className="w-2 h-2 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: color }}></div>}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* SATELLITE METRICS */}
            <div className="bg-white rounded-2xl shadow-lg border border-amber-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-12 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                    <span className="text-white">📡</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{t("Satellite Metrics")}</h3>
                    <p className="text-xs text-gray-500">{t("Real-time data")}</p>
                  </div>
                </div>
                <div className="relative group">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center cursor-help">
                    <img src="https://cdn-icons-png.flaticon.com/512/8316/8316158.png" alt="Info" className="w-4 h-4" />
                  </div>
                  <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    <div className="font-medium mb-2">{t("Satellite Metrics")}</div>
                    <div className="text-gray-300">{t("Real-time satellite data for vegetation, soil, and land conditions")}</div>
                    <div className="absolute -top-1 right-3"><div className="w-2 h-2 bg-gray-800 rotate-45"></div></div>
                  </div>
                </div>
              </div>
              {satelliteMetrics ? (
                <div className="space-y-3">
                  <MetricCard label={t("NDVI")} value={satelliteMetrics.data?.vegetation?.ndvi != null ? Number(satelliteMetrics.data.vegetation.ndvi).toFixed(2) : "N/A"} icon="🌱" color="green" explanationKey="NDVI Explanation" />
                  <MetricCard label={t("Soil Moisture")} value={satelliteMetrics.data?.vegetation?.soil_moisture != null ? Number(satelliteMetrics.data.vegetation.soil_moisture).toFixed(2) : "N/A"} icon="💧" color="blue" explanationKey="Soil Moisture Explanation" />
                  <MetricCard label={t("Land Surface Temp")} value={`${satelliteMetrics.data?.land?.lst != null ? Math.round(satelliteMetrics.data.land.lst) : "N/A"}°C`} icon="🌡️" color="red" explanationKey="Land Surface Temp Explanation" />
                  <MetricCard label={t("Land Cover Type")} value={satelliteMetrics.data?.land?.land_cover ?? "N/A"} icon="🗺️" color="purple" explanationKey="Land Cover Explanation" />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3"><span className="text-xl">📡</span></div>
                  <p className="text-gray-500 text-sm">{t("Select a location to view satellite metrics")}</p>
                </div>
              )}
            </div>
          </div>

          {/* ══ MAP COLUMN ══ */}
          <div className="lg:col-span-6 flex flex-col gap-3">

            {/* MAP */}
            <div className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-gray-800">{t("Optimal Locations Map")}</h3>
                  {activeFilter !== "top3" && (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-semibold"
                      style={{ backgroundColor: activeFilterConfig?.color }}>
                      <span>{activeFilterConfig?.emoji}</span>
                      <span>{t(activeFilterConfig?.label)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="h-[680px] relative">
                <MapContainer center={[44.0, 17.0]} zoom={7} style={{ height: "100%", width: "100%" }} className="rounded-b-2xl">
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution='&copy; OpenStreetMap' />
                  {visibleForageZones.map((zone) => {
                    const color = PLANT_COLORS[zone.plant_type] || "#22c55e"
                    return (
                      <Circle key={`zone-${zone.id}`} center={[zone.latitude, zone.longitude]} radius={zone.radius} pathOptions={{ color, fillColor: color, fillOpacity: 0.18, weight: 2.5 }}>
                        <Popup>
                          <div className="p-2 min-w-[160px]">
                            <div className="font-bold text-gray-800 mb-1">{zone.name}</div>
                            <div className="text-xs text-gray-500 mb-2">{t(zone.country)}</div>
                            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ backgroundColor: color + "20" }}>
                              <img src={PLANT_ICONS[zone.plant_type] || PLANT_ICONS["Wildflower"]} alt={zone.plant_type} className="w-5 h-5 object-contain" />
                              <span className="font-semibold text-sm" style={{ color }}>{t(zone.plant_type)}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1.5">{t("Zone radius")}: {(zone.radius / 1000).toFixed(0)} km</div>
                          </div>
                        </Popup>
                      </Circle>
                    )
                  })}
                  {userHives.map(hive => (
                    <Marker key={`hive-${hive.id}`} position={[hive.lat, hive.lng]} icon={hiveIcon} eventHandlers={{ click: () => setSelected({ ...hive, source: "hive" }) }}>
                      <Popup className="custom-popup"><div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200 shadow-md"><div className="font-bold text-gray-800 text-sm">{hive.name || "Hive Location"}</div></div></Popup>
                    </Marker>
                  ))}
                  {activeFilter === "top3" && (
                    <>
                      {recommendations.Bosnia?.map((rec, i) => (
                        <Marker key={`bosnia-${i}`} position={[rec.lat, rec.lng]} icon={redIcon} eventHandlers={{ click: () => { setSelected({ ...rec, source: "gpt", country: "Bosnia" }); setActiveCountry("Bosnia") } }}>
                          <Popup className="custom-popup"><div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200 shadow-md"><div className="font-bold text-gray-800 text-sm">{rec.name}</div><div className="text-xs text-gray-500 mt-1">{t("Bosnia")}</div></div></Popup>
                        </Marker>
                      ))}
                      {recommendations.Croatia?.map((rec, i) => {
                        const lat = rec.name === "Konavle" ? 42.5561 : rec.lat
                        const lng = rec.name === "Konavle" ? 18.3184 : rec.lng
                        return (
                          <Marker key={`croatia-${i}`} position={[lat, lng]} icon={blueIcon} eventHandlers={{ click: () => { setSelected({ ...rec, source: "gpt", country: "Croatia" }); setActiveCountry("Croatia") } }}>
                            <Popup className="custom-popup"><div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200 shadow-md"><div className="font-bold text-gray-800 text-sm">{rec.name}</div><div className="text-xs text-gray-500 mt-1">{t("Croatia")}</div></div></Popup>
                          </Marker>
                        )
                      })}
                      {recommendations.Serbia?.map((rec, i) => (
                        <Marker key={`serbia-${i}`} position={[rec.lat, rec.lng]} icon={greenIcon} eventHandlers={{ click: () => { setSelected({ ...rec, source: "gpt", country: "Serbia" }); setActiveCountry("Serbia") } }}>
                          <Popup className="custom-popup"><div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200 shadow-md"><div className="font-bold text-gray-800 text-sm">{rec.name}</div><div className="text-xs text-gray-500 mt-1">{t("Serbia")}</div></div></Popup>
                        </Marker>
                      ))}
                    </>
                  )}
                  {selected && <FlyToMarker lat={selected.lat} lng={selected.lng} />}
                </MapContainer>
              </div>
            </div>

            {/* FORECAST */}
            <div className="bg-white rounded-2xl shadow-lg border border-amber-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center"><span className="text-white">📅</span></div>
                <div>
                  <h3 className="font-bold text-gray-800">{t("7-Day Forecast")}</h3>
                  <p className="text-xs text-gray-500">{selected ? t("Weather forecast for") + " " + selected.name : t("Weather forecast for selected location")}</p>
                </div>
              </div>
              {forecast ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                    {forecast.map((day, i) => (
                      <div key={i} className="bg-gradient-to-b from-amber-50 to-white rounded-xl p-2.5 text-center border border-amber-100 hover:border-amber-300 transition-all">
                        <div className="font-semibold text-gray-800 mb-1 text-xs">{getDayName(day.date)}</div>
                        <div className="text-[10px] text-gray-500 mb-1 flex justify-center">{formatDate(day.date)}</div>
                        <div className="text-xl mb-1">{getForecastIcon(day)}</div>
                        <div className="flex justify-center gap-1 text-xs mb-1">
                          <span className="text-blue-600 font-semibold">{day.temp_min || day.temp_min === 0 ? Math.round(day.temp_min) : "N/A"}°</span>
                          <span className="text-red-600 font-semibold">{day.temp_max || day.temp_max === 0 ? Math.round(day.temp_max) : "N/A"}°</span>
                        </div>
                        <div className="text-[10px] text-gray-600">{day.precip || day.precip === 0 ? `${day.precip}mm` : "N/A"}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center items-center gap-8 mt-3 pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-full bg-blue-600"></div><span className="text-gray-600">{t("Lowest temp")}</span></div>
                    <div className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-full bg-red-600"></div><span className="text-gray-600">{t("Highest temp")}</span></div>
                    <div className="flex items-center gap-1 text-[10px]"><span className="text-gray-600">{t("mm = rainfall")}</span></div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3"><span className="text-xl">🌤️</span></div>
                  <p className="text-gray-500 text-sm">{t("Select a location to view the forecast")}</p>
                </div>
              )}
            </div>
          </div>

          {/* ══ RIGHT SIDEBAR ══ */}
          <div className="lg:col-span-3 flex flex-col gap-6">

            {/* WEATHER IMAGE */}
            <div className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                <h3 className="font-bold text-gray-800">{t("Current Weather")}</h3>
                <p className="text-xs text-gray-500">{t("Current weather conditions")}</p>
              </div>
              <div className="aspect-video relative">
                <img src={getWeatherImage() || "/placeholder.svg"} alt="Current weather" className="w-full h-full object-contain"
                  onError={e => { e.target.src = "/placeholder.svg?height=200&width=300" }} />
                {weather && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{getWeatherIcon(weather.description)}</span>
                      <div>
                        <div className="text-white font-bold text-2xl">{Math.round(weather.temp)}°C</div>
                        <div className="text-amber-200 text-sm capitalize">{t(weather.description)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* WEATHER DATA */}
            <div className="bg-white rounded-2xl shadow-lg border border-amber-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center"><span className="text-white">📊</span></div>
                <div>
                  <h3 className="font-bold text-gray-800">{t("Weather Data")}</h3>
                  <p className="text-xs text-gray-500">{t("Detailed weather information")}</p>
                </div>
              </div>
              {selected && weather ? (
                <div className="space-y-3">
                  <WeatherMetric icon="💨" label={t("Wind Speed")} value={`${Math.round(weather.wind_speed)} m/s`} color="green" />
                  <WeatherMetric icon="💧" label={t("Humidity")} value={`${weather.humidity || "N/A"}%`} color="cyan" />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3"><span className="text-xl">📍</span></div>
                  <p className="text-gray-500 text-sm">{t("Select a location to view weather data")}</p>
                </div>
              )}
            </div>

            {/* PLANTS BLOOMING */}
            <div className="bg-white rounded-2xl shadow-lg border border-amber-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center"><span className="text-white">🌼</span></div>
                <div>
                  <h3 className="font-bold text-gray-800">{t("Plants in Bloom")}</h3>
                  <p className="text-xs text-gray-500">{t("Currently blooming")}</p>
                </div>
              </div>
              {selected?.plants ? (
                <div className="space-y-2">
                  {selected.plants.slice(0, 3).map((plant, index) => {
                    const color = PLANT_COLORS[plant] || "#22c55e"
                    return (
                      <div key={index} className="flex items-center gap-3 p-3 rounded-lg border transition-all"
                        style={{ backgroundColor: color + "10", borderColor: color + "40" }}>
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm flex-shrink-0">
                          <img src={PLANT_ICONS_LOCAL[plant] || PLANT_ICONS_LOCAL["Wildflower"]} alt={plant} className="w-5 h-5 object-contain"
                            onError={e => { e.target.src = PLANT_ICONS_LOCAL["Wildflower"] }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-800 text-sm truncate">{t(plant)}</h4>
                          <p className="text-xs text-gray-500">{t("Active blooming")}</p>
                        </div>
                        <div className="text-xs px-2 py-1 rounded-full font-medium flex-shrink-0" style={{ backgroundColor: color + "25", color }}>
                          {index === 0 ? t("Excellent") : index === 1 ? t("Medium") : t("Low")}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3"><span className="text-xl">🌱</span></div>
                  <p className="text-gray-500 text-sm">{t("Select a location to view blooming data")}</p>
                </div>
              )}
            </div>

            <BeeConditionsCard weather={weather} />

          </div>
        </div>
      </main>
    </div>
  )
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

const MetricCard = ({ label, value, icon, color, explanationKey }) => {
  const { t } = useTranslation()
  const colorClasses = { green: "bg-green-100 text-green-700", blue: "bg-blue-100 text-blue-700", red: "bg-red-100 text-red-700", purple: "bg-purple-100 text-purple-700" }
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-gray-50 to-white border border-gray-100">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg ${colorClasses[color]} flex items-center justify-center flex-shrink-0`}><span className="text-base">{icon}</span></div>
        <h4 className="font-medium text-gray-700 text-xs">{label}</h4>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-bold text-gray-900 text-sm">{value}</span>
        <div className="relative group">
          <div className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center cursor-help transition-colors">
            <img src="https://cdn-icons-png.flaticon.com/512/8316/8316158.png" alt="Info" className="w-3 h-3 opacity-70" />
          </div>
          <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 w-56 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[9999]">
            <div className="font-medium mb-1">{label}</div>
            <div className="text-gray-300 leading-relaxed">{t(explanationKey)}</div>
            <div className="absolute top-1/2 right-full transform -translate-y-1/2 -mr-1"><div className="w-2 h-2 bg-gray-800 rotate-45"></div></div>
          </div>
        </div>
      </div>
    </div>
  )
}

const WeatherMetric = ({ icon, label, value, color }) => {
  const colorClasses = { red: "from-red-100 to-orange-50 border-red-100", blue: "from-blue-100 to-cyan-50 border-blue-100", green: "from-green-100 to-emerald-50 border-green-100", cyan: "from-cyan-100 to-blue-50 border-cyan-100" }
  return (
    <div className={`p-3 rounded-xl bg-gradient-to-r ${colorClasses[color]} border`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{icon}</span>
          <span className="font-medium text-gray-700 text-sm">{label}</span>
        </div>
        <span className="font-bold text-gray-900">{value}</span>
      </div>
    </div>
  )
}

export default RecommendationMap